/**
 * Utility functions for document comments
 */

/**
 * Check if a user has permission to manage comments
 * @param {Object} userProfile - The current user profile
 * @param {boolean} isMobile - Whether the user is on a mobile device
 * @returns {boolean} - Whether the user can manage comments
 */
export function hasCommentPermission(userProfile, isMobile = false) {
  // Comments are disabled on mobile
  if (isMobile) return false;
  
  // User must be authenticated and have a profile
  if (!userProfile) return false;
  
  // Only users (not parties) can manage comments
  if (userProfile.type !== 'user') return false;
  
  // Check allowed roles
  const allowedRoles = ['agent', 'assistant', 'tc', 'manager', 'managerassistant'];
  return allowedRoles.includes(userProfile.role);
}

/**
 * Check if a user can manage a specific comment (edit, resolve, delete)
 * @param {Object} userProfile - The current user profile
 * @param {Object} comment - The comment object
 * @returns {boolean} - Whether the user can manage this comment
 */
export function canManageComment(userProfile, comment) {
  if (!userProfile || !comment) return false;
  
  // User can manage their own comments
  if (comment.createdBy.uid === userProfile.id) return true;
  
  // Managers and manager assistants can manage all comments
  if (['manager', 'managerassistant'].includes(userProfile.role)) return true;
  
  return false;
}

/**
 * Get the display color for a comment status
 * @param {string} status - The comment status
 * @returns {string} - The semantic UI color
 */
export function getCommentStatusColor(status) {
  switch (status) {
    case 'active': return 'blue';
    case 'resolved': return 'green';
    case 'deleted': return 'red';
    default: return 'grey';
  }
}

/**
 * Format a timestamp for display
 * @param {Object} timestamp - Firebase timestamp (can be Firestore Timestamp, serialized timestamp, or Date)
 * @returns {string} - Formatted date string
 */
export function formatCommentDate(timestamp) {
  if (!timestamp) return "Unknown";

  try {
    let date;

    // Handle Firestore Timestamp object with toDate() method
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle serialized Firestore timestamp from cloud functions (_seconds and _nanoseconds)
    else if (timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Handle serialized timestamp with seconds/nanoseconds (alternative format)
    else if (timestamp.seconds !== undefined) {
      date = new Date(timestamp.seconds * 1000);
    }
    // Handle Date object or ISO string
    else {
      date = new Date(timestamp);
    }

    // Validate the date
    if (isNaN(date.getTime())) {
      return "Unknown";
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return "Unknown";
  }
}

/**
 * Generate CSV content for comment export
 * @param {Array} comments - Array of comment objects
 * @param {string} documentName - Name of the document
 * @returns {string} - CSV content
 */
export function generateCommentCSV(comments, documentName) {
  const headers = [
    "Date/Time",
    "Page",
    "Status", 
    "Author Name",
    "Author Role",
    "Comment",
    "Resolved By",
    "Resolved Date",
    "Deleted By", 
    "Deleted Date"
  ];

  const rows = comments.map(comment => [
    formatCommentDate(comment.createdAt),
    comment.pageNumber,
    comment.status,
    `${comment.createdBy.firstName} ${comment.createdBy.lastName}`,
    comment.createdBy.role,
    `"${comment.comment.replace(/"/g, '""')}"`, // Escape quotes in CSV
    comment.resolvedBy ? `${comment.resolvedBy.firstName} ${comment.resolvedBy.lastName}` : "",
    comment.resolvedAt ? formatCommentDate(comment.resolvedAt) : "",
    comment.deletedBy ? `${comment.deletedBy.firstName} ${comment.deletedBy.lastName}` : "",
    comment.deletedAt ? formatCommentDate(comment.deletedAt) : ""
  ]);

  return [headers, ...rows].map(row => row.join(",")).join("\n");
}

/**
 * Download CSV file
 * @param {string} csvContent - The CSV content
 * @param {string} filename - The filename for download
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
